import Flickity from "flickity";
import "flickity/css/flickity.css";

import "flickity-fade";
import "flickity-fade/flickity-fade.css";

function parseCarouselSettings(carouselEl) {
  try {
    return JSON.parse(
      carouselEl.getAttribute("data-carousel-settings") || "{}",
    );
  } catch (error) {
    console.warn("Invalid data-carousel-settings JSON.", error);
    return null;
  }
}

function normalizeItemHeights(carouselItemEls) {
  if (!carouselItemEls.length) return;

  let tallestHeight = 0;

  carouselItemEls.forEach((carouselItemEl) => {
    tallestHeight = Math.max(tallestHeight, carouselItemEl.offsetHeight);
  });

  carouselItemEls.forEach((carouselItemEl) => {
    carouselItemEl.style.height = `${tallestHeight}px`;
  });
}

function shouldDisableDesktop(carouselSettings, carouselItemEls) {
  return (
    window.innerWidth > 1120 &&
    !!carouselSettings.disableItemsDesktop &&
    carouselSettings.disableItemsDesktop >= carouselItemEls.length
  );
}

function buildFlickityOptions(carouselSettings) {
  const isFade = !!carouselSettings.fade;
  const isFreeScroll = !!carouselSettings.freeScroll && !isFade;
  const isAutoScroll =
    !!carouselSettings.autoScroll &&
    !isFade &&
    !isFreeScroll &&
    !carouselSettings.autoPlay;
  const isAutoPlay = !!carouselSettings.autoPlay && !isAutoScroll;

  return {
    isFade,
    isFreeScroll,
    isAutoScroll,
    isAutoPlay,
    flickityOptions: {
      accessibility: true,
      resize: true,
      percentPosition: true,
      setGallerySize: true,
      fade: isFade,
      cellAlign:
        !isFade && !isFreeScroll
          ? carouselSettings.cellAlign || "center"
          : "left",
      contain: !isFade && !isFreeScroll ? !!carouselSettings.contain : false,
      freeScroll: isFreeScroll,
      wrapAround:
        typeof carouselSettings.wrapAround === "boolean"
          ? carouselSettings.wrapAround
          : true,
      draggable:
        typeof carouselSettings.draggable === "boolean"
          ? carouselSettings.draggable
          : true,
      prevNextButtons: false,
      pageDots:
        typeof carouselSettings.pageDots === "boolean"
          ? carouselSettings.pageDots
          : false,
      autoPlay: isAutoPlay ? carouselSettings.autoPlay : false,
      pauseAutoPlayOnHover:
        typeof carouselSettings.pauseAutoPlayOnHover === "boolean"
          ? carouselSettings.pauseAutoPlayOnHover
          : true,
      imagesLoaded:
        typeof carouselSettings.imagesLoaded === "boolean"
          ? carouselSettings.imagesLoaded
          : true,
    },
  };
}

function setupOverflow(carouselEl, carouselListEl, carouselSettings, isFade) {
  if (!carouselSettings.showOverFlow || isFade) return;

  const flickityViewportEl = carouselListEl.querySelector(".flickity-viewport");

  carouselListEl.style.overflow = "visible";

  if (flickityViewportEl) {
    flickityViewportEl.style.overflow = "visible";
  }
}

function setupNavigation(
  carouselEl,
  carouselNavEl,
  carouselPrevBtnEl,
  carouselNextBtnEl,
  flickityInstance,
  carouselSettings,
) {
  if (carouselNavEl) {
    carouselNavEl.style.display = carouselSettings.showNavigation ? "" : "none";
  }

  if (carouselPrevBtnEl) {
    carouselPrevBtnEl.addEventListener("click", () => {
      flickityInstance.previous();
    });
  }

  if (carouselNextBtnEl) {
    carouselNextBtnEl.addEventListener("click", () => {
      flickityInstance.next();
    });
  }

  if (flickityInstance.slides.length <= 1 && carouselNavEl) {
    carouselNavEl.style.display = "none";
  }
}

function setupAutoScroll(carouselEl, flickityInstance, carouselSettings) {
  let requestId = null;
  let isPaused = false;

  const autoScrollSpeed =
    typeof carouselSettings.autoScrollSpeed === "number"
      ? carouselSettings.autoScrollSpeed
      : 0.7;

  flickityInstance.x = 0;
  flickityInstance.positionSlider();

  function play() {
    if (isPaused) return;

    flickityInstance.x -= autoScrollSpeed;
    flickityInstance.positionSlider();

    requestId = window.requestAnimationFrame(play);
  }

  function start() {
    if (requestId) return;
    isPaused = false;
    requestId = window.requestAnimationFrame(play);
  }

  function pause() {
    isPaused = true;

    if (requestId) {
      window.cancelAnimationFrame(requestId);
      requestId = null;
    }
  }

  start();

  if (carouselSettings.pauseAutoPlayOnHover !== false) {
    carouselEl.addEventListener("mouseenter", pause);
    carouselEl.addEventListener("mouseleave", start);
    carouselEl.addEventListener("focusin", pause);
    carouselEl.addEventListener("focusout", start);
    carouselEl.addEventListener("touchstart", pause, { passive: true });
    carouselEl.addEventListener("touchend", start);
  }

  window.addEventListener("blur", pause);
  window.addEventListener("focus", start);

  return {
    start,
    pause,
    destroy() {
      pause();
      window.removeEventListener("blur", pause);
      window.removeEventListener("focus", start);
    },
  };
}

function initCarousel(carouselEl) {
  const carouselListEl = carouselEl.querySelector("[data-carousel-list]");
  const carouselItemEls = [
    ...carouselEl.querySelectorAll("[data-carousel-item]"),
  ];
  const carouselNavEl = carouselEl.querySelector(".carousel__nav");
  const carouselPrevBtnEl = carouselEl.querySelector("[data-carousel-prev]");
  const carouselNextBtnEl = carouselEl.querySelector("[data-carousel-next]");

  if (!carouselListEl) return null;

  const carouselSettings = parseCarouselSettings(carouselEl);
  if (!carouselSettings) return null;

  carouselListEl.style.display = "block";

  normalizeItemHeights(carouselItemEls);

  if (shouldDisableDesktop(carouselSettings, carouselItemEls)) {
    carouselListEl.style.display = "";
    carouselListEl.classList.add("carousel__list--disable");

    if (carouselNavEl) {
      carouselNavEl.remove();
    }

    return null;
  }

  const { isFade, isAutoScroll, flickityOptions } =
    buildFlickityOptions(carouselSettings);

  const flickityInstance = new Flickity(carouselListEl, flickityOptions);

  setupOverflow(carouselEl, carouselListEl, carouselSettings, isFade);

  setupNavigation(
    carouselEl,
    carouselNavEl,
    carouselPrevBtnEl,
    carouselNextBtnEl,
    flickityInstance,
    carouselSettings,
  );

  let autoScrollControls = null;

  if (flickityInstance.slides.length > 1 && isAutoScroll) {
    autoScrollControls = setupAutoScroll(
      carouselEl,
      flickityInstance,
      carouselSettings,
    );
  }

  return {
    element: carouselEl,
    list: carouselListEl,
    flickity: flickityInstance,
    autoScroll: autoScrollControls,
    destroy() {
      if (autoScrollControls) {
        autoScrollControls.destroy();
      }

      flickityInstance.destroy();
    },
  };
}

export function initCarousels() {
  const carouselEls = document.querySelectorAll("[data-carousel]");
  if (!carouselEls.length) return [];

  if (typeof Flickity === "undefined") {
    console.warn("Flickity is not loaded.");
    return [];
  }

  return [...carouselEls]
    .map((carouselEl) => initCarousel(carouselEl))
    .filter(Boolean);
}

document.addEventListener("DOMContentLoaded", () => {
  console.log("hello");
  initCarousels();
});
